import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { gameAPI } from "../services/api";
import { InventoryItem, ClickResponse, Enemy, BattleState } from "../types";
import InventoryGrid from "./InventoryGrid";
import ClickArea from "./ClickArea";

const GameScreen: React.FC = () => {
  const { user } = useAuth();
  const [inventory, setInventory] = useState<InventoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [clicking, setClicking] = useState(false);
  const [lastDrop, setLastDrop] = useState<ClickResponse["droppedItem"] | null>(
    null
  );
  const [currentEnemy, setCurrentEnemy] = useState<Enemy | null>(null);
  const [currentEnemyHealth, setCurrentEnemyHealth] = useState(0);
  const [battleState, setBattleState] = useState<BattleState | null>(null);
  const [damageNumbers, setDamageNumbers] = useState<
    Array<{ id: number; damage: number; timestamp: number }>
  >([]);
  const [enemyShakeType, setEnemyShakeType] = useState<
    "damage" | "death" | null
  >(null);

  useEffect(() => {
    loadGameData();
  }, []);

  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.code === "Space" || event.code === "Enter") {
        event.preventDefault();
        handleClick();
      }
    };

    window.addEventListener("keydown", handleKeyPress);
    return () => {
      window.removeEventListener("keydown", handleKeyPress);
    };
  }, [clicking, currentEnemy, currentEnemyHealth]);

  const loadGameData = async () => {
    try {
      const [inventoryResponse, battleResponse] = await Promise.all([
        gameAPI.getInventory(),
        gameAPI.getBattleState(),
      ]);

      setInventory(inventoryResponse.inventory);
      setBattleState(battleResponse.battleState);
      setCurrentEnemy(battleResponse.battleState?.current_enemy || null);
      setCurrentEnemyHealth(
        battleResponse.battleState?.current_enemy?.max_health || 0
      );
    } catch (error) {
      console.error("Failed to load game data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleClick = async () => {
    console.log("Handle click called", {
      clicking,
      currentEnemy,
      currentEnemyHealth,
    });
    if (clicking || !currentEnemy) {
      console.log("Click blocked:", { clicking, currentEnemy });
      return;
    }

    setClicking(true);

    try {
      // Calculate damage on client side (base damage + random factor - defense)
      const baseDamage = 10;
      const randomFactor = Math.floor(Math.random() * 10) + 1;
      const damageDealt = Math.max(
        1,
        baseDamage + randomFactor - currentEnemy.defense
      );

      console.log("Damage calculated:", {
        baseDamage,
        randomFactor,
        defense: currentEnemy.defense,
        damageDealt,
      });

      // Add new damage number with unique ID
      const damageId = Date.now() + Math.random();
      setDamageNumbers((prev) => [
        ...prev,
        { id: damageId, damage: damageDealt, timestamp: Date.now() },
      ]);

      // Remove damage number after animation completes
      setTimeout(() => {
        setDamageNumbers((prev) => prev.filter((dmg) => dmg.id !== damageId));
      }, 1200);

      // Update enemy health on client side
      const newHealth = Math.max(0, currentEnemyHealth - damageDealt);
      setCurrentEnemyHealth(newHealth);

      // Set shake type based on whether enemy dies
      if (newHealth <= 0) {
        setEnemyShakeType("death");
        setTimeout(() => setEnemyShakeType(null), 800);
      } else {
        setEnemyShakeType("damage");
        setTimeout(() => setEnemyShakeType(null), 300);
      }

      // Check if enemy is defeated
      if (newHealth <= 0) {
        console.log("Enemy defeated!");

        // Call API to handle enemy defeat (drop item, spawn new enemy)
        const response = await gameAPI.defeatEnemy(currentEnemy.id);

        if (response.droppedItem) {
          setLastDrop(response.droppedItem);
          // Refresh inventory
          const inventoryResponse = await gameAPI.getInventory();
          setInventory(inventoryResponse.inventory);
          // Clear drop notification after 3 seconds
          setTimeout(() => setLastDrop(null), 3000);
        }

        // Update battle state and spawn new enemy
        if (response.battleState) {
          setBattleState(response.battleState);
          // Use the enemy from battle state to ensure we have the complete data
          if (response.battleState.current_enemy) {
            setCurrentEnemy(response.battleState.current_enemy);
            setCurrentEnemyHealth(
              response.battleState.current_enemy.max_health
            );
          }
        }
      }
    } catch (error) {
      console.error("Attack failed:", error);
    } finally {
      setClicking(false);
    }
  };

  if (loading) {
    return (
      <div className="game-screen loading">
        <div className="loading-spinner">Loading game...</div>
      </div>
    );
  }

  return (
    <div className="game-screen">
      <div className="game-header">
        <div className="title-container">
          <img src="/retomon-logo.jpg" alt="RETOMON" className="title-logo" />
          <h1>RETOMON - PLAYING AS {user?.username}!</h1>
          <img src="/retomon-logo.jpg" alt="RETOMON" className="title-logo" />
        </div>
      </div>

      <div className="game-content">
        <div className="left-panel">
          <ClickArea
            onClick={handleClick}
            clicking={clicking}
            lastDrop={lastDrop || null}
            currentEnemy={currentEnemy}
            currentEnemyHealth={currentEnemyHealth}
            damageNumbers={damageNumbers}
            enemyShakeType={enemyShakeType}
          />
        </div>

        <div className="right-panel">
          <InventoryGrid inventory={inventory} />
        </div>
      </div>
    </div>
  );
};

export default GameScreen;
